import { useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtMoney, todayISO } from '../lib/format';
import type { QuoteLine, QuotePreview } from '../lib/types';
import { Button, Callout, Dialog, Field, Input, useToast } from './ui';
import { CustomerPicker } from './CustomerPicker';
import { IconPlus, IconTrash, IconUpload } from './icons';
import { useOpenDocument } from './Layout';

const VAT_RATE = 0.18;
const r2 = (n: number) => Math.round(n * 100) / 100;
const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(/[,₪\s]/g, '')));

export function QuoteImportButton({ children, customerId, variant }: { children: ReactNode; customerId?: number; variant?: 'default' | 'primary' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)}>{children}</Button>
      {open ? <QuoteImportDialog customerId={customerId} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/**
 * Price quote prepared in Excel: upload → the sheet is read (customer, date, number,
 * items, totals) → the user checks and fixes everything → saved as a quote in the customer's file.
 */
export function QuoteImportDialog({ customerId, onClose }: { customerId?: number; onClose: () => void }) {
  const [preview, setPreview] = useState<QuotePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // form
  const [customer, setCustomer] = useState<number | null>(customerId ?? null);
  const [number, setNumber] = useState('');
  const [issueDate, setIssueDate] = useState(todayISO());
  const [lines, setLines] = useState<Array<{ description: string; quantity: string; unitPrice: string; total: string }>>([]);
  const [subtotal, setSubtotal] = useState('');
  const [vat, setVat] = useState('');
  const [total, setTotal] = useState('');
  const [notes, setNotes] = useState('');
  const qc = useQueryClient();
  const openDoc = useOpenDocument();

  const upload = async (file: File) => {
    if (!/\.xlsx$/i.test(file.name)) {
      toast.error(new Error('יש לבחור קובץ Excel בפורמט xlsx'));
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const p = await api<QuotePreview>('/api/quotes/import/preview', { form });
      setPreview(p);
      if (!customerId) setCustomer(p.suggestedCustomerId);
      setNumber(p.parsed.number ?? '');
      setIssueDate(p.parsed.issueDate ?? todayISO());
      setLines(p.parsed.lineItems.map((l) => ({ description: l.description, quantity: l.quantity?.toString() ?? '', unitPrice: l.unitPrice?.toString() ?? '', total: l.total?.toString() ?? '' })));
      setSubtotal(p.parsed.subtotal?.toString() ?? '');
      setVat(p.parsed.vat?.toString() ?? '');
      setTotal(p.parsed.total?.toString() ?? '');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const recalc = () => {
    const sub = r2(lines.reduce((s, l) => s + (num(l.total) ?? (num(l.unitPrice) ?? 0) * (num(l.quantity) ?? 1)), 0));
    const v = r2(sub * VAT_RATE);
    setSubtotal(String(sub));
    setVat(String(v));
    setTotal(String(r2(sub + v)));
  };
  const setLine = (i: number, patch: Partial<(typeof lines)[number]>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async () => {
    if (!preview || !customer) return;
    setBusy(true);
    try {
      const lineItems: QuoteLine[] = lines
        .filter((l) => l.description.trim())
        .map((l) => ({ description: l.description.trim(), quantity: num(l.quantity), unitPrice: num(l.unitPrice), total: num(l.total) }));
      const r = await api<{ documentId: number; number: string }>('/api/quotes/import/commit', {
        body: {
          fileId: preview.fileId,
          customerId: customer,
          number: number.trim() || null,
          issueDate,
          lineItems,
          subtotal: num(subtotal) ?? 0,
          vat: num(vat) ?? 0,
          total: num(total) ?? 0,
          notes: notes.trim() || null,
          customerNameInFile: preview.parsed.customerName,
        },
      });
      for (const k of ['documents', 'customer', 'customers', 'dashboard', 'workplan']) qc.invalidateQueries({ queryKey: [k] });
      toast.success(`הצעת מחיר ${r.number} נשמרה בתיק הלקוח`);
      onClose();
      openDoc(r.documentId);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const totalsOk = num(subtotal) != null && num(total) != null && Math.abs((num(subtotal) ?? 0) + (num(vat) ?? 0) - (num(total) ?? 0)) <= 1;

  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title="ייבוא הצעת מחיר מ-Excel"
      footer={
        preview ? (
          <>
            <Button variant="primary" onClick={save} loading={busy} disabled={!customer || !issueDate || !(num(total) != null)}>שמירת הצעת המחיר</Button>
            <Button onClick={() => setPreview(null)}>קובץ אחר</Button>
            <Button variant="ghost" onClick={onClose}>ביטול</Button>
          </>
        ) : (
          <Button onClick={onClose}>ביטול</Button>
        )
      }
    >
      {!preview ? (
        <div
          className={`dropzone ${dragging ? 'over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = e.dataTransfer.files[0];
            if (f) upload(f);
          }}
          onClick={() => input.current?.click()}
          role="button"
          tabIndex={0}
        >
          <IconUpload size={22} />
          <strong>{busy ? 'קורא את הקובץ…' : 'גררו לכאן את קובץ ה-Excel של ההצעה, או לחצו לבחירה'}</strong>
          <span className="small muted">המערכת מזהה טבלת פריטים (תיאור / כמות / מחיר / סה״כ), לקוח, תאריך, מספר הצעה וסכומים — ותציג הכל לבדיקה לפני שמירה.</span>
          <input ref={input} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </div>
      ) : (
        <div className="stack">
          {preview.alreadyImported ? <Callout tone="warn">הקובץ הזה כבר יובא בעבר כהצעת מחיר {preview.alreadyImported.number}.</Callout> : null}
          {preview.parsed.warnings.length ? (
            <Callout tone="info" title="לבדיקה">
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>{preview.parsed.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </Callout>
          ) : null}
          <div className="form-grid">
            <Field label={`לקוח${preview.parsed.customerName ? ` (בקובץ: ${preview.parsed.customerName})` : ''}`} required className="span-2">
              <CustomerPicker value={customer} onChange={(id) => setCustomer(id)} />
            </Field>
            <Field label="תאריך ההצעה" required>
              <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </Field>
            <Field label="מספר הצעה" hint="ריק = מספר אוטומטי">
              <Input value={number} onChange={(e) => setNumber(e.target.value)} ltr />
            </Field>
          </div>

          <div className="table-wrap panel">
            <table className="table">
              <thead>
                <tr>
                  <th>תיאור</th>
                  <th className="num" style={{ width: 90 }}>כמות</th>
                  <th className="num" style={{ width: 120 }}>מחיר ליח׳</th>
                  <th className="num" style={{ width: 120 }}>סה״כ</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td><Input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} /></td>
                    <td><Input value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} ltr /></td>
                    <td><Input value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} ltr /></td>
                    <td><Input value={l.total} onChange={(e) => setLine(i, { total: e.target.value })} ltr /></td>
                    <td>
                      <Button size="sm" variant="ghost" icon onClick={() => setLines(lines.filter((_, j) => j !== i))} aria-label="מחיקת שורה"><IconTrash size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="panel-foot row">
              <Button size="sm" variant="ghost" onClick={() => setLines([...lines, { description: '', quantity: '', unitPrice: '', total: '' }])}><IconPlus /> שורה</Button>
              <span className="spacer" />
              <Button size="sm" onClick={recalc} title="סכום השורות + מע״מ 18%">חישוב סכומים מהשורות</Button>
            </div>
          </div>

          <div className="form-grid">
            <Field label="סה״כ לפני מע״מ" required>
              <Input value={subtotal} onChange={(e) => setSubtotal(e.target.value)} ltr />
            </Field>
            <Field label={`מע״מ${preview.parsed.vatAssumed ? ' (חושב 18%)' : ''}`}>
              <Input value={vat} onChange={(e) => setVat(e.target.value)} ltr />
            </Field>
            <Field label="סה״כ כולל מע״מ" required>
              <Input value={total} onChange={(e) => setTotal(e.target.value)} ltr />
            </Field>
            <Field label="הערות (לא חובה)">
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
          {!totalsOk ? <p className="small" style={{ color: 'var(--warn)' }}>לפני מע״מ + מע״מ אינו שווה לסה״כ — כדאי לבדוק.</p> : <p className="small muted">סה״כ: {fmtMoney(num(total))} · קובץ: {preview.fileName}{preview.parsed.sheet ? ` (גיליון "${preview.parsed.sheet}")` : ''}</p>}
        </div>
      )}
    </Dialog>
  );
}
